import * as React from "react";
import throttle from "lodash/throttle";
import cx from "classnames";
import { isBrowser } from "@vingle/wtf-sdk/helpers/envChecker";

const styles = require("./thresholdSticky.scss");

interface IProps {
  stickyThreshold: number;
  disable?: boolean;
  scrollTarget?: "window" | "parent" | "self";
  stickedTopOffset?: number | string;
  childrenWrapperClassName?: string;
}

interface IState {
  isSticky: boolean;
  isMounted: boolean;
}

export default class ThresholdSticky extends React.PureComponent<
  IProps,
  IState
> {
  public state: IState = {
    isSticky: false,
    isMounted: false,
  };

  private readonly refWrapper = React.createRef<HTMLDivElement>();
  private storedScrollPosition = 0;

  public componentDidMount() {
    this.setScrollEvent("attach");
    this.setState({
      isMounted: true,
    });
  }

  public componentWillUnmount() {
    this.setScrollEvent("detach");
  }

  public render() {
    return (
      <div className={styles.wrapper} ref={this.refWrapper}>
        {this.renderChildren()}
      </div>
    );
  }

  private readonly renderChildren = () => {
    const {
      stickedTopOffset = 0,
      disable,
      childrenWrapperClassName,
    } = this.props;
    const { isSticky, isMounted } = this.state;
    const enableSticky = isSticky && !disable;
    const style: React.CSSProperties = {
      top: stickedTopOffset,
    };
    return (
      <div className={styles.childWrapper}>
        <div
          style={{
            ...style,
            animationDuration: isMounted ? undefined : "0ms",
          }}
          className={cx(
            styles.stickedChildrenWrapper,
            childrenWrapperClassName,
            {
              [styles.stickedChildrenWrapperExit]: !enableSticky,
              [styles.stickedChildrenWrapperEnter]: enableSticky,
            },
          )}
        >
          {this.props.children}
        </div>
        {this.props.children}
      </div>
    );
  };

  private readonly setScrollEvent = (state: "attach" | "detach") => {
    if (!isBrowser()) {
      return;
    }
    const { scrollTarget } = this.props;

    const targetElement = (() => {
      const wrapper = this.refWrapper.current;
      if (!wrapper) {
        return window;
      }
      switch (scrollTarget) {
        case "parent": {
          return wrapper.parentElement || window;
        }
        case "self": {
          return wrapper;
        }
        case "window":
        default: {
          return window;
        }
      }
    })();

    if (state === "attach") {
      targetElement.addEventListener("scroll", this.setStickyStateHandler);
    } else {
      targetElement.removeEventListener("scroll", this.setStickyStateHandler);
    }
  };

  // tslint:disable-next-line:member-ordering
  private readonly setStickyStateHandler = throttle((e: any) => {
    const { stickyThreshold } = this.props;
    const currentScrollPosition = (() => {
      const target = e.target;
      if (target) {
        if (target.scrollTop !== undefined) {
          return target.scrollTop;
        } else if (target.scrollingElement !== undefined) {
          return target.scrollingElement.scrollTop;
        }
      }
      return 0;
    })();
    const direction = this.setScrollDirection(currentScrollPosition);

    if (currentScrollPosition > stickyThreshold && direction === "up") {
      this.changeStateOfSticky(true);
    } else {
      this.changeStateOfSticky(false);
    }
  }, 80);

  private readonly setScrollDirection = (currentScrollPosition: number) => {
    const calculatedScrollDirection =
      currentScrollPosition - this.storedScrollPosition;
    this.storedScrollPosition = currentScrollPosition;
    return calculatedScrollDirection > 0 ? "down" : "up";
  };

  private readonly changeStateOfSticky = (state: boolean) => {
    if (this.state.isSticky !== state) {
      this.setState({
        isSticky: state,
      });
    }
  };
}
